import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { TRUST } from "@/data/site";

function Counter({ to, suffix = "", duration = 1.6 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setN(to); return; }
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduce]);

  return <span ref={ref}>{n}{suffix}</span>;
}

export default function YearsSection() {
  return (
    <section className="py-24 md:py-36 bg-[color:var(--bg)] relative overflow-hidden" data-testid="years-section">
      <div className="absolute inset-0 halftone-bg opacity-30 halftone-fade" aria-hidden />
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--text-2)] mb-6">— The Numbers</div>
            <div className="font-display leading-[0.85] tracking-[-0.04em] flex items-start gap-4 md:gap-6">
              <span className="text-[26vw] md:text-[380px] dot-number">
                <Counter to={30} duration={2} />
              </span>
              <span className="text-[10vw] md:text-[140px] text-[color:var(--accent)] leading-none pt-6">+</span>
            </div>
            <div className="mt-6 font-display text-3xl md:text-5xl leading-tight tracking-tight max-w-[640px]">
              Years spent quietly perfecting the craft of print.
            </div>
          </div>
          <div className="lg:col-span-5 space-y-8">
            {TRUST.map((t, i) => (
              <motion.div
                key={t.v}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.09 }}
                className="border-t border-black/10 pt-6 flex items-start justify-between gap-6"
              >
                <div className="font-display text-4xl md:text-5xl">{t.k}</div>
                <div className="text-[color:var(--text-2)] text-[14px] max-w-[220px] text-right leading-[1.6]">{t.v}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
